import React from 'react';
import { Shield, Zap, Users, Globe, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { useLanguage } from '../i18n';

export const Applications = () => {
  const { copy } = useLanguage();
  const benefitIcons = [Zap, Shield, Globe, Users];
  const benefits = copy.applications.benefits.map(([title, description], index) => ({
    icon: benefitIcons[index],
    title,
    description,
  }));

  const technologies = [
    {
      category: 'Frontend',
      items: ['React', 'Vue.js', 'Angular', 'Next.js', 'TypeScript', 'Tailwind CSS']
    },
    {
      category: 'Backend',
      items: ['Node.js', 'Python/Django', '.NET Core', 'PHP/Laravel', 'Java/Spring', 'Ruby on Rails']
    },
    {
      category: 'Mobile',
      items: ['React Native', 'Flutter', 'Swift (iOS)', 'Kotlin (Android)', 'Ionic', 'Xamarin']
    },
    {
      category: copy.applications.database,
      items: ['PostgreSQL', 'MongoDB', 'MySQL', 'Redis', 'Elasticsearch', 'Firebase']
    },
    {
      category: 'Cloud & DevOps',
      items: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'CI/CD']
    }
  ];

  const faqItems = copy.applications.faq.map(([question, answer]) => ({ question, answer }));

  return (
    <section id="applications" className="py-24 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium inline-block">
            {copy.applications.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold">
            {copy.applications.title}<span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">{copy.applications.accent}</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {copy.applications.intro}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-orange-200 dark:hover:border-orange-900">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 group-hover:scale-110 transition-transform">
                    <Icon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {technologies.map((tech, index) => (
            <Card key={index} className="border-2 hover:border-orange-200 dark:hover:border-orange-900 transition-all">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-orange-600 dark:text-orange-400">{tech.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.items.map((item, iIndex) => (
                    <span
                      key={iIndex}
                      className="px-3 py-1 rounded-full bg-muted text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors cursor-default"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <h3 className="text-3xl font-bold">{copy.applications.faqTitle}</h3>
            <p className="text-muted-foreground">{copy.applications.faqIntro}</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-2 rounded-lg px-6 hover:border-orange-200 dark:hover:border-orange-900 transition-colors">
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <span>{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6 pl-8">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
